import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaChartPie, FaUsers, FaShieldAlt, FaTrophy, FaBalanceScale, FaCog } from 'react-icons/fa';

const MutualFundsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Mutual Funds' }
  ];

  const fundTypes = [
    {
      icon: <FaChartPie className="w-8 h-8 text-secondary" />,
      title: "Equity Funds",
      description: "Diversified equity portfolios focusing on growth and capital appreciation across various market capitalizations."
    },
    {
      icon: <FaBalanceScale className="w-8 h-8 text-secondary" />,
      title: "Balanced Funds",
      description: "Optimal mix of equity and fixed income securities to provide balanced risk-return profiles."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Fixed Income Funds",
      description: "Conservative investment solutions focused on regular income generation and capital preservation."
    },
    {
      icon: <FaTrophy className="w-8 h-8 text-secondary" />,
      title: "Sector Specific Funds",
      description: "Specialized funds targeting specific sectors including technology, healthcare, and emerging markets."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-secondary" />,
      title: "Index Funds",
      description: "Low-cost passive investment strategies that track major market indices for broad market exposure."
    },
    {
      icon: <FaCog className="w-8 h-8 text-secondary" />,
      title: "Alternative Funds",
      description: "Innovative investment solutions including ESG funds, cryptocurrency funds, and private equity funds."
    }
  ];

  const benefits = [
    "Professional fund management by experienced investment teams",
    "Diversification across multiple asset classes and securities",
    "Liquidity with daily redemption and subscription options",
    "Transparent pricing and regular performance reporting",
    "Regulatory oversight ensuring investor protection",
    "Flexible investment amounts starting from minimum thresholds"
  ];

  return (
    <>
      <PageHeader 
        title="Mutual Funds" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/person-looking-finance-graphs_52683-116611.jpg_mhsxez.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Professional Mutual Fund Solutions
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <span className="font-semibold">Segway Capital</span> offers a comprehensive range of mutual fund solutions 
                designed to meet diverse investment objectives. Our professionally managed funds provide 
                investors with access to diversified portfolios, expert management, and institutional-quality 
                investment strategies.
              </p>
              <p className="text-lg text-gray-700">
                Whether you're seeking growth, income, or capital preservation, our mutual fund offerings 
                provide flexible investment options with varying risk profiles to align with your financial 
                goals and investment timeline.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/gradient-stock-market-concept_52683-76908.jpg_m8ry2v.jpg" 
                alt="Mutual Funds Analysis" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          {/* Fund Types */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">Our Fund Categories</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Explore our diverse range of mutual fund options, each designed to serve specific investment 
                objectives and risk preferences.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {fundTypes.map((fund, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
                >
                  <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    {fund.icon}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{fund.title}</h3>
                  <p className="text-gray-600">{fund.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/business-people-brainstorming-office_1048944-25233953.jpg_amz21m.jpg" 
                alt="Investment Team" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-6">Why Choose Our Mutual Funds?</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                    <div className="bg-secondary/10 rounded-full p-2 mr-4 mt-1">
                      <div className="w-2 h-2 bg-secondary rounded-full"></div>
                    </div>
                    <p className="text-gray-700 text-lg">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Start Investing in Mutual Funds Today</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Discover how our mutual fund solutions can help you achieve your investment goals with 
                professional management and diversified portfolios.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Explore Fund Options
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default MutualFundsPage; 